import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Portal Entry Point
 * 
 * This component handles token-based authentication from external websites.
 * When users are redirected from the main website with a token in the URL,
 * this component validates the token and automatically logs them in.
 * 
 * URL Format: /portal?token=JWT_TOKEN
 */
export default function Portal() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loginWithToken } = useAuth();
  const [status, setStatus] = useState('validating');
  const [error, setError] = useState(null);

  useEffect(() => {
    const validateAndLogin = async () => {
      const token = searchParams.get('token');

      // If already logged in, go directly to dashboard
      if (user) {
        navigate('/app', { replace: true });
        return;
      }

      // If no token provided, redirect to login
      if (!token) {
        navigate('/login', { replace: true });
        return;
      }

      try {
        // Validate token by calling /auth/me
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Token is valid - login the user
        if (response.data) {
          loginWithToken(token, response.data);
          setStatus('success');
          // Small delay for visual feedback, then redirect
          setTimeout(() => {
            navigate('/app', { replace: true });
          }, 500);
        }
      } catch (err) {
        setStatus('error');
        setError('Invalid or expired token. Please login again.');
        // Redirect to login after showing error
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
      }
    };

    validateAndLogin();
  }, [searchParams, user, navigate, loginWithToken]);

  return (
    <div className="min-h-screen bg-[#F9F7F5] flex items-center justify-center" data-testid="portal-page">
      <div className="text-center">
        {status === 'validating' && (
          <>
            <Loader2 className="w-12 h-12 text-[#4A3B32] animate-spin mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-[#2C2420] mb-2">
              Validating access...
            </h2>
            <p className="text-[#5D524D]">
              Please wait while we verify your credentials.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="font-heading text-2xl text-[#2C2420] mb-2">
              Access Granted
            </h2>
            <p className="text-[#5D524D]">
              Redirecting to dashboard...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="font-heading text-2xl text-[#2C2420] mb-2">
              Access Denied
            </h2>
            <p className="text-[#5D524D]">
              {error}
            </p>
            <p className="text-sm text-[#8C8078] mt-2">
              Redirecting to login...
            </p>
          </>
        )}
      </div>
    </div>
  );
}
